/**
 * Secure Storage Utilities
 * Provides secure wrapper functions for localStorage with validation and sanitization
 */

import {
  sanitizeString,
  sanitizeTaskName,
  sanitizeProjectName,
  sanitizeTagName,
  sanitizeClientName,
  sanitizeSprintName,
  sanitizeId,
  sanitizeBoolean,
  sanitizeTimeString,
  isSafeForStorage,
} from "./inputSanitization.js";

/**
 * Storage keys used in the application
 * Centralized to prevent typos and ensure consistency
 */
export const STORAGE_KEYS = {
  START_TIME: "startTime",
  IS_RUNNING: "isRunning",
  TASK: "task",
  PROJECT: "project",
  PROJECT_ID: "projectId",
  TAG: "tag",
  TAG_ID: "tagId",
  CLIENT: "client",
  CLIENT_ID: "clientId",
  IS_BILLABLE: "isBillable",
  SPRINT_NAME: "sprintName",
  SPRINT_ID: "sprintId",
  RUNNING_TASK: "runningTask",
};

/**
 * Secure setItem wrapper that validates and sanitizes data before storage
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @param {Object} options - Options for sanitization
 * @returns {boolean} True if successful, false otherwise
 */
export const secureSetItem = (key, value, options = {}) => {
  try {
    // Check if value is safe for storage
    if (!isSafeForStorage(value)) {
      console.warn(`[SecureStorage] Attempted to store unsafe value for key: ${key}`);
      return false;
    }

    // Sanitize based on key type
    let sanitizedValue = value;

    switch (key) {
      case STORAGE_KEYS.TASK:
        sanitizedValue = sanitizeTaskName(value);
        break;
      case STORAGE_KEYS.PROJECT:
        sanitizedValue = sanitizeProjectName(value);
        break;
      case STORAGE_KEYS.TAG:
        sanitizedValue = sanitizeTagName(value);
        break;
      case STORAGE_KEYS.CLIENT:
        sanitizedValue = sanitizeClientName(value);
        break;
      case STORAGE_KEYS.SPRINT_NAME:
        sanitizedValue = sanitizeSprintName(value);
        break;
      case STORAGE_KEYS.PROJECT_ID:
      case STORAGE_KEYS.TAG_ID:
      case STORAGE_KEYS.CLIENT_ID:
      case STORAGE_KEYS.SPRINT_ID:
      case STORAGE_KEYS.RUNNING_TASK:
        sanitizedValue = sanitizeId(value);
        if (!sanitizedValue && value) {
          console.warn(`[SecureStorage] Invalid ID format for key: ${key}`);
          return false;
        }
        break;
      case STORAGE_KEYS.IS_RUNNING:
      case STORAGE_KEYS.IS_BILLABLE:
        sanitizedValue = sanitizeBoolean(value);
        break;
      case STORAGE_KEYS.START_TIME:
        // Time values should be ISO strings or timestamps
        if (typeof value === "string") {
          sanitizedValue = sanitizeString(value, { maxLength: 100 });
        } else if (typeof value === "number") {
          sanitizedValue = value;
        } else {
          sanitizedValue = String(value);
        }
        break;
      default:
        // Generic sanitization for unknown keys
        if (typeof value === "string") {
          sanitizedValue = sanitizeString(value);
        }
    }

    // Stringify and store
    const serialized = JSON.stringify(sanitizedValue);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`[SecureStorage] Error storing key ${key}:`, error);
    return false;
  }
};

/**
 * Secure getItem wrapper that validates and sanitizes data after retrieval
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} Retrieved and sanitized value or defaultValue
 */
export const secureGetItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    
    if (item === null || item === undefined) {
      return defaultValue;
    }

    // Check for invalid values
    if (item === "undefined" || item === "null") {
      return defaultValue;
    }

    // Parse JSON
    let parsed;
    try {
      parsed = JSON.parse(item);
    } catch (e) {
      console.warn(`[SecureStorage] Invalid JSON for key ${key}, returning default`);
      return defaultValue;
    }

    // Sanitize based on key type
    switch (key) {
      case STORAGE_KEYS.TASK:
        return sanitizeTaskName(parsed) || defaultValue;
      case STORAGE_KEYS.PROJECT:
        return sanitizeProjectName(parsed) || defaultValue;
      case STORAGE_KEYS.TAG:
        return sanitizeTagName(parsed) || defaultValue;
      case STORAGE_KEYS.CLIENT:
        return sanitizeClientName(parsed) || defaultValue;
      case STORAGE_KEYS.SPRINT_NAME:
        return sanitizeSprintName(parsed) || defaultValue;
      case STORAGE_KEYS.PROJECT_ID:
      case STORAGE_KEYS.TAG_ID:
      case STORAGE_KEYS.CLIENT_ID:
      case STORAGE_KEYS.SPRINT_ID:
      case STORAGE_KEYS.RUNNING_TASK:
        const sanitizedId = sanitizeId(parsed);
        return sanitizedId || defaultValue;
      case STORAGE_KEYS.IS_RUNNING:
      case STORAGE_KEYS.IS_BILLABLE:
        return sanitizeBoolean(parsed);
      default:
        return parsed;
    }
  } catch (error) {
    console.error(`[SecureStorage] Error retrieving key ${key}:`, error);
    return defaultValue;
  }
};

/**
 * Secure removeItem wrapper
 * @param {string} key - Storage key to remove
 * @returns {boolean} True if successful
 */
export const secureRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`[SecureStorage] Error removing key ${key}:`, error);
    return false;
  }
};

/**
 * Clear all timer-related storage items securely
 * @param {Array<string>} keys - Optional array of keys to clear (defaults to all timer keys)
 * @returns {boolean} True if successful
 */
export const clearTimerStorage = (keys = Object.values(STORAGE_KEYS)) => {
  try {
    keys.forEach((key) => {
      secureRemoveItem(key);
    });
    return true;
  } catch (error) {
    console.error("[SecureStorage] Error clearing timer storage:", error);
    return false;
  }
};

/**
 * Validate that stored data is still valid
 * @param {string} key - Storage key to validate
 * @returns {boolean} True if data is valid
 */
export const validateStoredData = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item || item === "undefined" || item === "null") {
      return false;
    }
    
    JSON.parse(item);
    return true;
  } catch (e) {
    return false;
  }
};
